<template>
  <div v-if="currentUser" class="visits-container">
    <div class="page-header">
      <h1>Wizyty klientów</h1>
      <VisitsFilters @getVisits="getVisits" :visits="visits" :visible-visits-type="visibleVisitsType" />
    </div>

    <div class="visits-layout">
      <div class="visits-list-section">
        <SectionCard>
          <HintComponent page-key="business_panel_calendar_visits_preview" :absolute="true" />
          <template #header>
            Lista wizyt specjalisty
          </template>

          <div class="visits-list" v-if="visits[visibleVisitsType].items.length > 0">
            <div
                v-for="visit in visits[visibleVisitsType].items"
                :key="visit.id"
                class="visit-card"
                :class="visit.status"
            >
              <div class="visit-card__top">
                <div>
                  <h3>{{ formatDate(visit.date) }} • {{ visit.time || '-' }}</h3>
                  <div class="meta-row">
                  <div class="status-pill" :class="getStatusClass(visit.status)">
                    {{ getStatusLabel(visit.status) }}
                  </div>
                  <div class="meta-chip">ID wizyty: #{{ visit.id }}</div>
                  </div>
                </div>
                <div class="visit-advance-status" v-if="visit.payment?.order">
                  <AdvancePaymentPrice :price="visit.payment.order.price"/>
                  <AdvancePaymentStatus :key="visit.id" :payment-status="visit.payment.status" :visit-status="visit.status" :payment-id="visit.payment.id" :calendar-item-id="visit.id" />
                </div>
              </div>

              <div class="visit-card__grid">
                <p class="meta-item">
                  <span class="meta-label">Klient</span>
                  <span class="meta-value">{{ visit.user?.name || '-' }}</span>
                </p>
                <p class="meta-item meta-item--wide">
                  <span class="meta-label">Informacje</span>
                  <span class="meta-value">{{ visit.visit_info || 'Brak dodatkowych informacji' }}</span>
                </p>
              </div>

              <div class="visit-card__actions" v-if="visit.status === 'open' && visit.outdated">
                <button type="button" class="btn btn-warning" @click="closeAsNoShow(visit)">
                  Zamknij jako nieodbytą
                </button>
                <button type="button" class="btn btn-success" @click="closeAsFinished(visit)">
                  Zamknij jako zakończoną
                </button>
              </div>
              <div class="visit-card__actions" v-else-if="visit.status === 'completed'">
                <div v-if="visit.rated" class="visit-card__info">
                  Ocena wizyty została już wystawiona
                </div>
                <div v-if="!visit.rated && visit.review_links[0]" class="visit-card__info">
                  Link do oceny został już wysłany w dniu {{ new Date(visit.review_links[0].created_at).toLocaleDateString() }}. <br>
                  Wizyta jeszcze nie została oceniona.
                </div>
                <button v-if="!visit.rated && !visit.review_links[0]" type="button" class="btn btn-success" @click="sendRatingLink(visit)">
                  Wyślij link do oceny
                </button>
                <button v-if="!visit.rated && visit.review_links[0]" type="button" class="btn btn-success" @click="sendRatingLink(visit)">
                  Wyślij ponownie link do oceny
                </button>
              </div>
            </div>
          </div>

          <div v-else class="empty-state">
            {{ isLoading ? 'Ładowanie wizyt...' : 'Brak wizyt do wyświetlenia.' }}
          </div>

          <div class="pagination" v-if="visits[visibleVisitsType].pagination.lastPage > 1">
            <button
                type="button"
                class="btn-page"
                :disabled="visits[visibleVisitsType].pagination.currentPage <= 1 || isLoading"
                @click="getVisits(visits[visibleVisitsType].pagination.currentPage - 1, visibleVisitsType)"
            >
              Poprzednia
            </button>

            <span class="pagination__info">
              Strona {{ visits[visibleVisitsType].pagination.currentPage }} z {{ visits[visibleVisitsType].pagination.lastPage }}
            </span>

            <button
                type="button"
                class="btn-page"
                :disabled="visits[visibleVisitsType].pagination.currentPage >= visits[visibleVisitsType].pagination.lastPage || isLoading"
                @click="getVisits(visits[visibleVisitsType].pagination.currentPage + 1, visibleVisitsType)"
            >
              Następna
            </button>
          </div>
        </SectionCard>
      </div>
    </div>
  </div>
  <div v-else>
    <h4>Ładowanie...</h4>
  </div>
</template>

<script>
import { useMeta } from 'vue-meta';
import axios from 'axios';
import { useAuthStore } from '@/stores/auth';
import { useAppStore } from '@/stores/app';
import SectionCard from '@/components/Cards/SectionCard.vue';
import AdvancePaymentStatus from "@/components/SpecialComponents/AdvancePaymentStatus.vue";
import AdvancePaymentPrice from "@/components/SpecialComponents/AdvancePaymentPrice.vue";
import VisitsFilters from "@/components/SpecialComponents/VisitsFilters.vue";
import HintComponent from "@/components/SpecialComponents/HintComponent.vue";

export default {
  name: 'BizProfileVisits',
  setup() {
    useMeta({ title: 'Wizyty klientów' });

    return {
      authStore: useAuthStore(),
      appStore: useAppStore(),
    }
  },
  data() {
    return {
      visits: {
        open: {
          items: [],
          pagination: {
            currentPage: 1,
            perPage: 15,
            lastPage: 1,
            total: 0
          }
        },
        closed: {
          items: [],
          pagination: {
            currentPage: 1,
            perPage: 15,
            lastPage: 1,
            total: 0
          }
        },
        completed: {
          items: [],
          pagination: {
            currentPage: 1,
            perPage: 15,
            lastPage: 1,
            total: 0
          }
        },
        canceled: {
          items: [],
          pagination: {
            currentPage: 1,
            perPage: 15,
            lastPage: 1,
            total: 0
          }
        }
      },
      visibleVisitsType: 'open',
      openedFinishPanelId: null,
      ratingLinks: {},
      isLoading: false,
      pagination: {
        currentPage: 1,
        perPage: 15,
        lastPage: 1,
        total: 0
      }
    }
  },
  computed: {
    currentUser() {
      return this.authStore.user;
    }
  },
  mounted() {
    if (!this.currentUser) {
      this.$router.push('/login');
      return;
    }

    if (this.currentUser.role_id !== 2 && this.currentUser.role_id !== 4) {
      this.$router.push('/login');
      return;
    }

    this.getVisits();
  },
  methods: {
    getVisits(page = 1, status = 'open') {
      this.isLoading = true;

      axios.get(`/CalendarItems/${status}`, {
        params: {
          page: page
        }
      }).then(res => {
        this.visits[status].items = res.data.data;

        this.visits[status].pagination.currentPage = res.data.current_page || page;
        this.visits[status].pagination.perPage = res.data.per_page || this.visits[status].pagination.perPage;
        this.visits[status].pagination.lastPage = res.data.last_page || 1;
        this.visits[status].pagination.total = res.data.total || this.visits[status].items.length;

        this.visibleVisitsType = status;
      }).catch(() => {
        this.appStore.setIsAlert('Nie udało się pobrać listy wizyt.');
      }).finally(() => {
        this.isLoading = false;
      });
    },
    getStatusLabel(status) {
      if (status === 'open') {
        return 'otwarta';
      }
      if (status === 'closed') {
        return 'zamknięta (klient nie przyszedł)';
      }
      if (status === 'completed') {
        return 'zamknięta (zakończona pomyślnie)';
      }
      if (status === 'canceled') {
        return 'zamknięta (anulowana)';
      }
      return status || 'nieznany';
    },
    getStatusClass(status) {
      if (status === 'closed') {
        return 'status-pill--closed';
      }
      if (status === 'completed') {
        return 'status-pill--completed';
      }
      if (status === 'canceled') {
        return 'status-pill--completed';
      }
      if (status === 'open') {
        return 'status-pill--open';
      }
      return 'status-pill--other';
    },
    formatDate(date) {
      if (!date) {
        return '-';
      }

      return String(date).split('T')[0];
    },
    closeAsNoShow(visit) {
      axios.post(`/MarkCalendarItemAsClosed/${visit.id}`).then(() => {
        this.appStore.setIsSuccess('Pomyślnie zmieniono status wizyty.');
        this.getVisits();
      }).catch(() => {
        this.appStore.setIsAlert('Wystąpił błąd podczas zmiany statusu wizyty.');
      });
    },
    closeAsFinished(visit) {
      axios.post(`/MarkCalendarItemAsCompleted/${visit.id}`).then(() => {
        this.appStore.setIsSuccess('Pomyślnie zmieniono status wizyty.');
        this.getVisits();
      }).catch(() => {
        this.appStore.setIsAlert('Wystąpił błąd podczas zmiany statusu wizyty.');
      });
    },
    async sendRatingLink(visit) {
      try {
        await axios.post(`SendReviewLink/${visit.id}`);
        this.appStore.setIsSuccess('Pomyślnie wysłano link do oceny.');
      } catch (e) {
        this.appStore.setIsAlert('Wystąpił błąd podczas wysyłania linku do oceny.');
      }
    }
  },
  components: {
    HintComponent,
    VisitsFilters,
    AdvancePaymentPrice, AdvancePaymentStatus,
    SectionCard
  }
}
</script>

<style scoped lang='scss'>
.visits-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 16px;
  font-family: sans-serif;
  box-sizing: border-box;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 32px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e5e7eb;
}

.page-header h1 {
  font-size: 2.8rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.visits-layout {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

.visits-list-section {
  flex: 1 1 0;
  min-width: 0;
}

.visits-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.visit-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px;
  background: #fff;
}

.visit-card.closed {
  border-color: #b7ead6;
  background: #f6fffb;
}

.visit-card.open {
  border-color: #ffd9bf;
  background: #fffaf6;
}

.visit-card h3 {
  margin: 0;
  font-size: 1.55rem;
  color: #1f2937;
  font-weight: 700;
}

.meta-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
}

.meta-chip {
  display: inline-flex;
  align-items: center;
  border: 1px solid #e5e7eb;
  color: #6b7280;
  border-radius: 999px;
  padding: 2px 9px;
  font-size: 1.2rem;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 1.2rem;
  font-weight: 700;
  border: 1px solid transparent;
}

.status-pill--open {
  color: #9a3412;
  background: #ffedd5;
  border-color: #fdba74;
}

.status-pill--completed {
  color: #065f46;
  background: #d1fae5;
  border-color: #86efac;
}

.status-pill--closed {
  color: #374151;
  background: #f3f4f6;
  border-color: #d1d5db;
}

.status-pill--canceled {
  color: #374151;
  background: #f3f4f6;
  border-color: #d1d5db;
}

.visit-card__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(160px, 1fr));
  gap: 8px 14px;
  margin-top: 10px;
}

.meta-item {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.meta-item--wide {
  grid-column: 1 / -1;
}

.meta-label {
  font-size: 1.15rem;
  color: #4b5563;
  text-transform: uppercase;
  letter-spacing: .03em;
}

.meta-value {
  font-size: 1.3rem;
  line-height: 1.35;
  color: #374151;
}

.visit-card__actions,
.finish-panel__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 10px;
}

.visit-card__info {
  color: #4b5563;
  font-size: 1.15rem;
}

.finish-panel {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #d1d5db;
}

.finish-panel label {
  font-size: 1.3rem;
  color: #4b5563;
}

.finish-panel input {
  width: 100%;
  margin-top: 6px;
  padding: 8px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1.3rem;
}

.btn {
  border: 0;
  border-radius: 8px;
  padding: 8px 12px;
  color: #fff;
  cursor: pointer;
  font-size: 1.3rem;
  font-weight: 600;
}

.btn-warning {
  background: #ef4444;
}

.btn-success {
  background: #16a34a;
}

.btn-primary {
  background: #2563eb;
}

.pagination {
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.pagination__info {
  font-size: 1.3rem;
  color: #6b7280;
}

.btn-page {
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  border-radius: 8px;
  padding: 7px 11px;
  cursor: pointer;
  font-size: 1.25rem;
  font-weight: 600;
}

.btn-page:disabled {
  opacity: .5;
  cursor: not-allowed;
}

.empty-state {
  text-align: center;
  padding: 24px;
  color: #9ca3af;
  font-size: 1.4rem;
}

.visit-advance-status {
  color: #9a3412;
  font-size: 1.2rem;
  font-weight: 600;
  border-radius: 6px;
  padding-top: 10px;
}
</style>